import React, { useState, useEffect } from "react";
import { X, Package, Clock, CheckCircle, AlertCircle, CreditCard, Receipt, Download, Settings, Palette, Calculator, ChevronRight } from "lucide-react";
import CartItem from "./CartItem";
import layableLogo from "./logo.png";
import { ChromePicker } from "react-color";

const AdminPage = () => {
  const [activeTab, setActiveTab] = useState("processing");
  const [orders, setOrders] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [showOrderDetail, setShowOrderDetail] = useState(false);
  const [showCouponModal, setShowCouponModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [newCoupon, setNewCoupon] = useState({
    couponName: "",
    type: "PERCENTAGE",
    discountAmount: 0,
    availableCount: 1,
  });
  const [editingOrder, setEditingOrder] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingOrders, setIsLoadingOrders] = useState(true);
  const [isLoadingCoupons, setIsLoadingCoupons] = useState(true);
  const [isUpdatingOrder, setIsUpdatingOrder] = useState(false);
  const [isUpdatingCoupon, setIsUpdatingCoupon] = useState(false);
  const [isCreatingCoupon, setIsCreatingCoupon] = useState(false);
  const [isDeletingCoupon, setIsDeletingCoupon] = useState(false);
  const [showAllOrders, setShowAllOrders] = useState({
    processing: false,
    completed: false,
    cancelled: false,
  });
  const [showAllCoupons, setShowAllCoupons] = useState(false);
  const [showPriceModal, setShowPriceModal] = useState(false);
  const [showColorModal, setShowColorModal] = useState(false);
  const [prices, setPrices] = useState({
    time: 0,
    pla: 0,
    abs: 0,
    pacf: 0,
    pc: 0,
    tpu: 0,
  });
  const [colors, setColors] = useState({
    pla: [],
    abs: [],
    pacf: [],
    pc: [],
    tpu: [],
  });
  const [selectedMaterial, setSelectedMaterial] = useState("pla");
  const [newColor, setNewColor] = useState("#000000");
  const [isUpdatingPrices, setIsUpdatingPrices] = useState(false);
  const [isUpdatingColors, setIsUpdatingColors] = useState(false);
  const [showCustomEstimateModal, setShowCustomEstimateModal] = useState(false);
  const [customEstimate, setCustomEstimate] = useState({
    file_name: "",
    material: "pla",
    estimated_cost: 0,
  });
  const [isCreatingEstimate, setIsCreatingEstimate] = useState(false);

  useEffect(() => {
    fetchOrders();
    fetchCoupons();
    fetchUserInfo();
    fetchPrices();
    fetchColors();
  }, []);

  const fetchOrders = async () => {
    setIsLoadingOrders(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE}/api/admin/orders`);
      const data = await response.json();
      setOrders(data);
    } catch (error) {
      console.error("Error fetching orders:", error);
    } finally {
      setIsLoadingOrders(false);
      setIsLoading(false);
    }
  };

  const fetchCoupons = async () => {
    setIsLoadingCoupons(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE}/api/admin/coupons`);
      const data = await response.json();
      setCoupons(data);
    } catch (error) {
      console.error("Error fetching coupons:", error);
    } finally {
      setIsLoadingCoupons(false);
      setIsLoading(false);
    }
  };

  const fetchUserInfo = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE}/api/admin/me`);
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      }
    } catch (error) {
      console.error("Error fetching user info:", error);
    }
  };

  const fetchPrices = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE}/api/admin/setting/prices`);
      if (response.ok) {
        const data = await response.json();
        setPrices(data);
      }
    } catch (error) {
      console.error("Error fetching prices:", error);
    }
  };

  const fetchColors = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE}/api/admin/setting/colors`);
      if (response.ok) {
        const data = await response.json();
        setColors(data);
      }
    } catch (error) {
      console.error("Error fetching colors:", error);
    }
  };

  const handlePriceChange = (field, value) => {
    setPrices((prev) => ({
      ...prev,
      [field]: parseInt(value) || 0,
    }));
  };

  const updatePrices = async () => {
    setIsUpdatingPrices(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE}/api/admin/setting/prices`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(prices),
      });

      if (response.ok) {
        setModalMessage("단가가 성공적으로 업데이트되었습니다.");
        setShowSuccessModal(true);
        setShowPriceModal(false);
      } else {
        setModalMessage("단가 업데이트에 실패했습니다.");
        setShowErrorModal(true);
      }
    } catch (error) {
      console.error("Error updating prices:", error);
      setModalMessage("단가 업데이트 중 오류가 발생했습니다.");
      setShowErrorModal(true);
    } finally {
      setIsUpdatingPrices(false);
    }
  };

  const addColor = () => {
    setColors((prev) => ({
      ...prev,
      [selectedMaterial]: [...prev[selectedMaterial], newColor],
    }));
    setNewColor("#000000");
  };
  const addClearColor = () => {
    setColors((prev) => ({
      ...prev,
      [selectedMaterial]: [...prev[selectedMaterial], "clear"],
    }));
  };

  const removeColor = (material, index) => {
    setColors((prev) => ({
      ...prev,
      [material]: prev[material].filter((_, i) => i !== index),
    }));
  };

  const updateColors = async () => {
    setIsUpdatingColors(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE}/api/admin/setting/colors`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(colors),
      });

      if (response.ok) {
        setModalMessage("색상이 성공적으로 업데이트되었습니다.");
        setShowSuccessModal(true);
        setShowColorModal(false);
      } else {
        setModalMessage("색상 업데이트에 실패했습니다.");
        setShowErrorModal(true);
      }
    } catch (error) {
      console.error("Error updating colors:", error);
      setModalMessage("색상 업데이트 중 오류가 발생했습니다.");
      setShowErrorModal(true);
    } finally {
      setIsUpdatingColors(false);
    }
  };

  const toggleShowAll = (section) => {
    setShowAllOrders((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const getDisplayedOrders = (orders, section) => {
    const filtered = orders.filter((order) => {
      if (section === "processing") return order.status === "처리중";
      if (section === "completed") return order.status === "완료";
      if (section === "cancelled") return order.status === "취소";
      return false;
    });
    const isMobile = window.innerWidth < 768; // Tailwind's md breakpoint
    return showAllOrders[section] ? filtered : filtered.slice(0, isMobile ? 6 : 3);
  };

  const getDisplayedCoupons = (coupons) => {
    return showAllCoupons ? coupons : coupons.slice(0, 10);
  };

  const handleOrderChange = (field, value) => {
    if (field.includes(".")) {
      const [parent, child] = field.split(".");
      setEditingOrder((prev) => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value,
        },
      }));
    } else {
      setEditingOrder((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const updateOrder = async () => {
    setIsUpdatingOrder(true);
    try {
      const updateData = {
        ...selectedOrder,
        status: editingOrder.status,
        tracking_no: editingOrder.tracking_no,
        user: {
          ...selectedOrder.user,
          ...editingOrder.user,
        },
        address: {
          ...selectedOrder.address,
          ...editingOrder.address,
        },
        memo: editingOrder.memo,
      };

      const response = await fetch(`${process.env.REACT_APP_API_BASE}/api/admin/order/${selectedOrder.uuid}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(updateData),
      });

      if (response.ok) {
        setSelectedOrder(updateData);
        await fetchOrders();
        setModalMessage("주문 정보가 성공적으로 업데이트되었습니다.");
        setShowSuccessModal(true);
        setShowOrderDetail(false);
      } else {
        setModalMessage("주문 정보 업데이트에 실패했습니다.");
        setShowErrorModal(true);
      }
    } catch (error) {
      console.error("Error updating order:", error);
      setModalMessage("주문 정보 업데이트 중 오류가 발생했습니다.");
      setShowErrorModal(true);
    } finally {
      setIsUpdatingOrder(false);
    }
  };

  const openOrderDetail = async (order) => {
    setSelectedOrder(order);
    setEditingOrder({
      ...order,
      tracking_no: order.tracking_no || "",
      user: {
        ...order.user,
        email: order.user.email || "",
      },
      address: {
        ...order.address,
      },
    });

    // 결제 정보 가져오기
    if (order.pay_code) {
      try {
        const response = await fetch(`${process.env.REACT_APP_PAY_BASE}/api/payment/${order.pay_code}/paid`);
        if (response.ok) {
          const paymentInfo = await response.json();
          setSelectedOrder((prev) => ({
            ...prev,
            paymentInfo,
          }));
        }
      } catch (error) {
        console.error("Error fetching payment info:", error);
      }
    }

    setShowOrderDetail(true);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "결제대기":
        return <Clock className="w-5 h-5 text-yellow-500" />;
      case "처리중":
        return <Package className="w-5 h-5 text-blue-500" />;
      case "완료":
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case "취소":
        return <AlertCircle className="w-5 h-5 text-red-500" />;
      default:
        return null;
    }
  };

  const getPaymentMethodInfo = (paymentData) => {
    if (!paymentData) return { text: "결제 정보 없음", icon: <AlertCircle className="w-5 h-5 text-gray-500" /> };

    const { publisher, cardname, type, brand, owner_type, number } = paymentData;

    let icon = <CreditCard className="w-5 h-5 text-blue-500" />;
    let text = "";
    let details = "";

    if (type === "CREDIT") {
      const ownerTypeText = owner_type === "PERSONAL" ? "개인" : "법인";
      text = `${cardname || publisher || "카드"} 결제`;
      details = `${ownerTypeText}신용(${brand}) ${number}`;
    } else if (type === "DEBIT") {
      const ownerTypeText = owner_type === "PERSONAL" ? "개인" : "법인";
      text = `${cardname || publisher || "카드"} 결제`;
      details = `${ownerTypeText}체크(${brand}) ${number}`;
    }

    return { text, icon, details };
  };

  const editCoupon = (coupon) => {
    setEditingCoupon(coupon);
    setNewCoupon({
      couponName: coupon.couponName,
      type: coupon.type,
      discountAmount: coupon.discountAmount,
      availableCount: coupon.availableCount,
    });
    setShowCouponModal(true);
  };

  const closeCouponModal = () => {
    setShowCouponModal(false);
    setEditingCoupon(null);
    setNewCoupon({
      couponName: "",
      type: "PERCENTAGE",
      discountAmount: 0,
      availableCount: 1,
    });
  };

  const createCoupon = async () => {
    setIsCreatingCoupon(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE}/api/admin/coupon`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(newCoupon),
      });

      if (response.ok) {
        await fetchCoupons();
        closeCouponModal();
        setModalMessage("쿠폰이 성공적으로 생성되었습니다.");
        setShowSuccessModal(true);
      } else {
        setModalMessage("쿠폰 생성에 실패했습니다.");
        setShowErrorModal(true);
      }
    } catch (error) {
      console.error("Error creating coupon:", error);
      setModalMessage("쿠폰 생성 중 오류가 발생했습니다.");
      setShowErrorModal(true);
    } finally {
      setIsCreatingCoupon(false);
    }
  };

  const updateCoupon = async () => {
    setIsUpdatingCoupon(true);
    try {
      const updateData = {
        couponId: editingCoupon.couponId,
        couponName: newCoupon.couponName,
        type: newCoupon.type,
        discountAmount: newCoupon.discountAmount,
        availableCount: newCoupon.availableCount,
      };

      const response = await fetch(`${process.env.REACT_APP_API_BASE}/api/admin/coupon`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(updateData),
      });

      if (response.ok) {
        await fetchCoupons();
        closeCouponModal();
        setModalMessage("쿠폰이 성공적으로 수정되었습니다.");
        setShowSuccessModal(true);
      } else {
        setModalMessage("쿠폰 수정에 실패했습니다.");
        setShowErrorModal(true);
      }
    } catch (error) {
      console.error("Error updating coupon:", error);
      setModalMessage("쿠폰 수정 중 오류가 발생했습니다.");
      setShowErrorModal(true);
    } finally {
      setIsUpdatingCoupon(false);
    }
  };

  const deleteCoupon = async (couponId) => {
    setIsDeletingCoupon(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE}/api/admin/coupon/${couponId}`, {
        method: "DELETE",
      });

      if (response.ok) {
        setModalMessage("쿠폰이 성공적으로 삭제되었습니다.");
        setShowSuccessModal(true);
        fetchCoupons(); // 쿠폰 목록 새로고침
      } else {
        setModalMessage("쿠폰 삭제에 실패했습니다.");
        setShowErrorModal(true);
      }
    } catch (error) {
      console.error("Error deleting coupon:", error);
      setModalMessage("쿠폰 삭제 중 오류가 발생했습니다.");
      setShowErrorModal(true);
    } finally {
      setIsDeletingCoupon(false);
    }
  };

  const handleCustomEstimateChange = (field, value) => {
    setCustomEstimate((prev) => ({
      ...prev,
      [field]: field === "estimated_cost" ? parseInt(value) || 0 : value,
    }));
  };

  const createCustomEstimate = async () => {
    setIsCreatingEstimate(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE}/api/admin/make_estimate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(customEstimate),
      });

      if (response.ok) {
        const data = await response.json();
        setModalMessage("커스텀 견적이 성공적으로 생성되었습니다.\n견적ID: " + data.uuid);
        setShowSuccessModal(true);
        setShowCustomEstimateModal(false);
        setCustomEstimate({
          file_name: "",
          material: "pla",
          estimated_cost: 0,
        });
      } else {
        setModalMessage("커스텀 견적 생성에 실패했습니다.");
        setShowErrorModal(true);
      }
    } catch (error) {
      console.error("Error creating custom estimate:", error);
      setModalMessage("커스텀 견적 생성 중 오류가 발생했습니다.");
      setShowErrorModal(true);
    } finally {
      setIsCreatingEstimate(false);
    }
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Header */}
      <header className="bg-white shadow-md">
        <div className="max-w-7xl mx-auto px-4 py-5 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <a href="//layable.kr">
              <img src={layableLogo} alt="logo" className="w-28 md:w-32" />
            </a>
            <h1 className="text-xl font-bold text-gray-400">3D 제작대행 Admin</h1>
          </div>
          <div className="flex items-center space-x-4">
            {user && (
              <div className="flex items-center space-x-2 text-gray-600">
                <a href="/cdn-cgi/access/logout">
                  <span className="font-medium">{user.common_name || user.email}</span>
                </a>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Mobile Tabs */}
        <div className="md:hidden mb-8">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 overflow-x-auto scrollbar-hide">
              <button onClick={() => setActiveTab("processing")} className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${activeTab === "processing" ? "border-teal-500 text-teal-600" : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"}`}>
                처리중인 주문
              </button>
              <button onClick={() => setActiveTab("completed")} className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${activeTab === "completed" ? "border-teal-500 text-teal-600" : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"}`}>
                완료된 주문
              </button>
              <button onClick={() => setActiveTab("cancelled")} className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${activeTab === "cancelled" ? "border-teal-500 text-teal-600" : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"}`}>
                취소된 주문
              </button>
              <button onClick={() => setActiveTab("coupons")} className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${activeTab === "coupons" ? "border-teal-500 text-teal-600" : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"}`}>
                쿠폰 관리
              </button>
              <button onClick={() => setActiveTab("settings")} className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${activeTab === "settings" ? "border-teal-500 text-teal-600" : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"}`}>
                설정
              </button>
            </nav>
          </div>
        </div>

        {/* Loading Spinner */}
        {isLoading && (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-teal-500"></div>
          </div>
        )}

        {/* Content Grid - PC Layout */}
        {!isLoading && (
          <div className="hidden md:grid md:grid-cols-5 gap-8">
            {/* Left Column - Orders */}
            <div className="space-y-8 col-span-3">
              {/* Processing Orders Section */}
              <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-teal-600">처리중인 주문</h2>
                  <span className="text-sm text-gray-500">{orders.filter((order) => order.status === "처리중").length}건</span>
                </div>
                {isLoadingOrders ? (
                  <div className="flex justify-center items-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-teal-500"></div>
                  </div>
                ) : orders.filter((order) => order.status === "처리중").length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <p>처리중인 주문이 없습니다.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {getDisplayedOrders(orders, "processing").map((order) => (
                      <div key={order.uuid} className="border rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer group" onClick={() => openOrderDetail(order)}>
                        <div className="flex justify-between items-start">
                          <div>
                            <h3 className="font-medium text-lg text-gray-900">{order.orderName || "주문"}</h3>
                            <div className="flex items-center space-x-2 mt-1">
                              <span className="text-xs text-gray-600 whitespace-nowrap">{order.user.name}</span>
                              <span className="text-xs text-gray-500">|</span>
                              <span className="text-xs text-gray-400 break-normal">{order.uuid}</span>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-medium text-lg text-teal-600">{order.cart.total_cost.toLocaleString()}원</p>
                            <p className="text-sm text-gray-500 mt-1 whitespace-nowrap">{order.cart.items.length}개 상품</p>
                          </div>
                        </div>
                        <div className="mt-3 flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <span className={`px-3 py-1 rounded-full text-sm flex items-center space-x-2 whitespace-nowrap ${order.status === "결제대기" ? "bg-yellow-100 text-yellow-800" : order.status === "처리중" ? "bg-blue-100 text-blue-800" : order.status === "완료" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>
                              {getStatusIcon(order.status)}
                              <span>{order.status}</span>
                            </span>
                            <span className="text-sm text-gray-500">{new Date(order.created_at).toLocaleString()}</span>
                          </div>
                          <div className="text-sm text-gray-400 group-hover:text-teal-600 transition-colors whitespace-nowrap">상세 정보 보기 →</div>
                        </div>
                      </div>
                    ))}
                    {orders.filter((order) => order.status === "처리중").length > 3 && (
                      <button onClick={() => toggleShowAll("processing")} className="w-full py-2 text-teal-600 hover:text-teal-800 text-sm font-medium">
                        {showAllOrders.processing ? "접기" : "더보기"}
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Completed Orders Section */}
              <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-teal-600">완료된 주문</h2>
                  <span className="text-sm text-gray-500">{orders.filter((order) => order.status === "완료").length}건</span>
                </div>
                {isLoadingOrders ? (
                  <div className="flex justify-center items-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-teal-500"></div>
                  </div>
                ) : orders.filter((order) => order.status === "완료").length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <p>완료된 주문이 없습니다.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {getDisplayedOrders(orders, "completed").map((order) => (
                      <div key={order.uuid} className="border rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer group" onClick={() => openOrderDetail(order)}>
                        <div className="flex justify-between items-start">
                          <div>
                            <h3 className="font-medium text-lg text-gray-900">{order.orderName || "주문"}</h3>
                            <div className="flex items-center space-x-2 mt-1">
                              <span className="text-xs text-gray-600">{order.user.name}</span>
                              <span className="text-xs text-gray-500">|</span>
                              <span className="text-xs text-gray-400 break-normal">{order.uuid}</span>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-medium text-lg text-teal-600">{order.cart.total_cost.toLocaleString()}원</p>
                            <p className="text-sm text-gray-500 mt-1">{order.cart.items.length}개 상품</p>
                          </div>
                        </div>
                        <div className="mt-3 flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <span className={`px-3 py-1 rounded-full text-sm flex items-center space-x-2 ${order.status === "결제대기" ? "bg-yellow-100 text-yellow-800" : order.status === "처리중" ? "bg-blue-100 text-blue-800" : order.status === "완료" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>
                              {getStatusIcon(order.status)}
                              <span>{order.status}</span>
                            </span>
                            <span className="text-sm text-gray-500">{new Date(order.created_at).toLocaleString()}</span>
                          </div>
                          <div className="text-sm text-gray-400 group-hover:text-teal-600 transition-colors whitespace-nowrap">상세 정보 보기 →</div>
                        </div>
                      </div>
                    ))}
                    {orders.filter((order) => order.status === "완료").length > 3 && (
                      <button onClick={() => toggleShowAll("completed")} className="w-full py-2 text-teal-600 hover:text-teal-800 text-sm font-medium">
                        {showAllOrders.completed ? "접기" : "더보기"}
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Cancelled Orders Section */}
              <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-teal-600">취소된 주문</h2>
                  <span className="text-sm text-gray-500">{orders.filter((order) => order.status === "취소").length}건</span>
                </div>
                {isLoadingOrders ? (
                  <div className="flex justify-center items-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-teal-500"></div>
                  </div>
                ) : orders.filter((order) => order.status === "취소").length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <p>취소된 주문이 없습니다.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {getDisplayedOrders(orders, "cancelled").map((order) => (
                      <div key={order.uuid} className="border rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer group" onClick={() => openOrderDetail(order)}>
                        <div className="flex justify-between items-start">
                          <div>
                            <h3 className="font-medium text-lg text-gray-900">{order.orderName || "주문"}</h3>
                            <div className="flex items-center space-x-2 mt-1">
                              <span className="text-xs text-gray-600">{order.user.name}</span>
                              <span className="text-xs text-gray-500">|</span>
                              <span className="text-xs text-gray-400 break-normal">{order.uuid}</span>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-medium text-lg text-teal-600">{order.cart.total_cost.toLocaleString()}원</p>
                            <p className="text-sm text-gray-500 mt-1">{order.cart.items.length}개 상품</p>
                          </div>
                        </div>
                        <div className="mt-3 flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <span className={`px-3 py-1 rounded-full text-sm flex items-center space-x-2 ${order.status === "결제대기" ? "bg-yellow-100 text-yellow-800" : order.status === "처리중" ? "bg-blue-100 text-blue-800" : order.status === "완료" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>
                              {getStatusIcon(order.status)}
                              <span>{order.status}</span>
                            </span>
                            <span className="text-sm text-gray-500">{new Date(order.created_at).toLocaleString()}</span>
                          </div>
                          <div className="text-sm text-gray-400 group-hover:text-teal-600 transition-colors whitespace-nowrap">상세 정보 보기 →</div>
                        </div>
                      </div>
                    ))}
                    {orders.filter((order) => order.status === "취소").length > 3 && (
                      <button onClick={() => toggleShowAll("cancelled")} className="w-full py-2 text-teal-600 hover:text-teal-800 text-sm font-medium">
                        {showAllOrders.cancelled ? "접기" : "더보기"}
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Right Column - Coupons and Settings */}
            <div className="bg-white rounded-xl shadow-lg p-6 col-span-2 flex flex-col">
              {/* Coupons Section - 80% height */}
              <div className="flex-1">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-bold text-teal-600">쿠폰 관리</h2>
                  <button onClick={() => setShowCouponModal(true)} className="px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 text-sm font-medium">
                    쿠폰 생성
                  </button>
                </div>
                {isLoadingCoupons ? (
                  <div className="flex justify-center items-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-teal-500"></div>
                  </div>
                ) : coupons.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <p>쿠폰이 없습니다.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {getDisplayedCoupons(coupons).map((coupon) => (
                      <div key={coupon.couponId} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                        <div className="flex justify-between items-start">
                          <div>
                            <h3 className="font-medium text-lg text-gray-900">{coupon.couponName}</h3>
                            <p className="text-xs text-gray-400 mt-1">쿠폰 ID: {coupon.couponId}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-medium text-lg text-teal-600">{coupon.type === "PERCENTAGE" ? `${coupon.discountAmount}% 할인` : `${coupon.discountAmount.toLocaleString()}원 할인`}</p>
                            <p className="text-sm text-gray-500 mt-1">남은 횟수: {coupon.availableCount}</p>
                          </div>
                        </div>
                        <div className="mt-3 flex justify-end space-x-2">
                          <button onClick={() => editCoupon(coupon)} disabled={isDeletingCoupon} className={`px-3 py-1 bg-teal-500 text-white rounded-lg hover:bg-teal-600 text-sm font-medium ${isDeletingCoupon ? "opacity-50 cursor-not-allowed" : ""}`}>
                            수정
                          </button>
                          <button onClick={() => deleteCoupon(coupon.couponId)} disabled={isDeletingCoupon} className={`px-3 py-1 bg-red-500 text-white rounded-lg hover:bg-red-600 text-sm font-medium ${isDeletingCoupon ? "opacity-50 cursor-not-allowed" : ""}`}>
                            {isDeletingCoupon ? (
                              <div className="flex items-center">
                                <div className="animate-spin rounded-full h-3 w-3 border-t-2 border-b-2 border-white mr-1"></div>
                                삭제 중...
                              </div>
                            ) : (
                              "삭제"
                            )}
                          </button>
                        </div>
                      </div>
                    ))}
                    {coupons.length > 10 && (
                      <button onClick={() => setShowAllCoupons(!showAllCoupons)} className="w-full py-2 text-teal-600 hover:text-teal-800 text-sm font-medium">
                        {showAllCoupons ? "접기" : "더보기"}
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Settings Section - 20% height */}
              <div className="mt-6 pt-6 border-t">
                <h3 className="text-lg font-semibold mb-2">설정</h3>
                <div className="grid grid-cols-3 gap-3">
                  <button onClick={() => setShowPriceModal(true)} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                    <div className="flex items-center gap-2">
                      <Settings className="w-5 h-5 text-teal-500" />
                      <span>단가 설정</span>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  </button>
                  <button onClick={() => setShowColorModal(true)} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                    <div className="flex items-center gap-2">
                      <Palette className="w-5 h-5 text-teal-500" />
                      <span>색상 설정</span>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  </button>
                  <button onClick={() => setShowCustomEstimateModal(true)} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                    <div className="flex items-center gap-2">
                      <Calculator className="w-5 h-5 text-teal-500" />
                      <span>임의견적</span>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Mobile Content */}
        {!isLoading && (
          <div className="md:hidden">
            {/* Mobile Orders List */}
            {activeTab !== "coupons" && activeTab !== "settings" && (
              <div className="grid gap-4">
                {isLoadingOrders ? (
                  <div className="flex justify-center items-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-teal-500"></div>
                  </div>
                ) : getDisplayedOrders(orders, activeTab).length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <p>주문 내역이 없습니다.</p>
                  </div>
                ) : (
                  <>
                    {getDisplayedOrders(orders, activeTab).map((order) => (
                      <div key={order.uuid} className="border rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer group" onClick={() => openOrderDetail(order)}>
                        <div className="flex justify-between items-start">
                          <div>
                            <h3 className="font-medium text-lg text-gray-900">{order.orderName || "주문"}</h3>
                            <div className="flex items-center space-x-2 mt-1">
                              <span className="text-xs text-gray-600">{order.user.name}</span>
                              <span className="text-xs text-gray-500">|</span>
                              <span className="text-xs text-gray-400 break-normal">{order.uuid}</span>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-medium text-lg text-teal-600">{order.cart.total_cost.toLocaleString()}원</p>
                            <p className="text-sm text-gray-500 mt-1">{order.cart.items.length}개 상품</p>
                          </div>
                        </div>
                        <div className="mt-3 flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <span className={`px-3 py-1 rounded-full text-sm flex items-center space-x-2 ${order.status === "결제대기" ? "bg-yellow-100 text-yellow-800" : order.status === "처리중" ? "bg-blue-100 text-blue-800" : order.status === "완료" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>
                              {getStatusIcon(order.status)}
                              <span>{order.status}</span>
                            </span>
                            <span className="text-sm text-gray-500">{new Date(order.created_at).toLocaleString()}</span>
                          </div>
                          <div className="text-sm text-gray-400 group-hover:text-teal-600 transition-colors whitespace-nowrap">상세 정보 보기 →</div>
                        </div>
                      </div>
                    ))}
                    {orders.filter((order) => {
                      if (activeTab === "processing") return order.status === "처리중";
                      if (activeTab === "completed") return order.status === "완료";
                      if (activeTab === "cancelled") return order.status === "취소";
                      return false;
                    }).length > 6 && (
                      <button onClick={() => toggleShowAll(activeTab)} className="w-full py-2 text-teal-600 hover:text-teal-800 text-sm font-medium">
                        {showAllOrders[activeTab] ? "접기" : "더보기"}
                      </button>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Mobile Settings Section */}
            {activeTab === "settings" && (
              <div className="space-y-4 ">
                <button onClick={() => setShowPriceModal(true)} className="w-full flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <div className="flex items-center gap-3">
                    <Settings className="w-5 h-5 text-teal-500" />
                    <div>
                      <p className="font-medium text-left">단가 설정</p>
                      <p className="text-sm text-left text-gray-500">시간당 단가와 소재별 단가를 설정합니다.</p>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </button>
                <button onClick={() => setShowColorModal(true)} className="w-full flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <div className="flex items-center gap-3">
                    <Palette className="w-5 h-5 text-teal-500" />
                    <div>
                      <p className="font-medium text-left">색상 설정</p>
                      <p className="text-sm text-left text-gray-500">소재별 사용 가능한 색상을 관리합니다.</p>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </button>
                <button onClick={() => setShowCustomEstimateModal(true)} className="w-full flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <div className="flex items-center gap-3">
                    <Calculator className="w-5 h-5 text-teal-500" />
                    <div>
                      <p className="font-medium text-left">임의견적</p>
                      <p className="text-sm text-left text-gray-500">임의의 커스텀 견적을 생성합니다.</p>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </button>
              </div>
            )}

            {/* Mobile Coupons List */}
            {activeTab === "coupons" && (
              <div className="space-y-4">
                {isLoadingCoupons ? (
                  <div className="flex justify-center items-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-teal-500"></div>
                  </div>
                ) : coupons.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <p>쿠폰이 없습니다.</p>
                  </div>
                ) : (
                  <div className="grid gap-4">
                    {getDisplayedCoupons(coupons).map((coupon) => (
                      <div key={coupon.couponId} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                        <div className="flex justify-between items-start">
                          <div>
                            <h3 className="font-medium text-lg text-gray-900">{coupon.couponName}</h3>
                            <p className="text-xs text-gray-400 mt-1">쿠폰 ID: {coupon.couponId}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-medium text-lg text-teal-600">{coupon.type === "PERCENTAGE" ? `${coupon.discountAmount}% 할인` : `${coupon.discountAmount.toLocaleString()}원 할인`}</p>
                            <p className="text-sm text-gray-500 mt-1">남은 횟수: {coupon.availableCount}</p>
                          </div>
                        </div>
                        <div className="mt-3 flex justify-end space-x-2">
                          <button onClick={() => editCoupon(coupon)} disabled={isDeletingCoupon} className={`px-3 py-1 bg-teal-500 text-white rounded-lg hover:bg-teal-600 text-sm font-medium ${isDeletingCoupon ? "opacity-50 cursor-not-allowed" : ""}`}>
                            수정
                          </button>
                          <button onClick={() => deleteCoupon(coupon.couponId)} disabled={isDeletingCoupon} className={`px-3 py-1 bg-red-500 text-white rounded-lg hover:bg-red-600 text-sm font-medium ${isDeletingCoupon ? "opacity-50 cursor-not-allowed" : ""}`}>
                            {isDeletingCoupon ? (
                              <div className="flex items-center">
                                <div className="animate-spin rounded-full h-3 w-3 border-t-2 border-b-2 border-white mr-1"></div>
                                삭제 중...
                              </div>
                            ) : (
                              "삭제"
                            )}
                          </button>
                        </div>
                      </div>
                    ))}
                    {coupons.length > 10 && (
                      <button onClick={() => setShowAllCoupons(!showAllCoupons)} className="w-full py-2 text-teal-600 hover:text-teal-800 text-sm font-medium">
                        {showAllCoupons ? "접기" : "더보기"}
                      </button>
                    )}
                  </div>
                )}
                <div className="flex justify-end mt-4">
                  <button onClick={() => setShowCouponModal(true)} className="px-6 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 text-lg font-medium">
                    쿠폰 생성
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </main>

      {/* Order Detail Modal */}
      {showOrderDetail && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2">
          <div className="bg-white rounded-xl shadow-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-2xl font-bold">주문 상세 정보</h2>
                <p className="text-sm text-gray-500 mt-1">주문번호: {selectedOrder?.uuid}</p>
              </div>
              <button onClick={() => setShowOrderDetail(false)} className="text-gray-500 hover:text-gray-700">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-6">
              {/* Order Status */}
              <div className="border rounded-lg p-4">
                <h3 className="font-medium mb-4">주문 상태</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {["결제대기", "처리중", "완료", "취소"].map((status) => (
                    <label key={status} className={`flex items-center space-x-2 p-3 rounded-lg cursor-pointer ${editingOrder?.status === status ? "bg-teal-50 border-teal-200" : "border border-gray-200 hover:border-teal-200"}`}>
                      <input type="radio" name="status" value={status} checked={editingOrder?.status === status} onChange={(e) => handleOrderChange("status", e.target.value)} className="text-teal-500 focus:ring-teal-500" />
                      <div className="flex items-center">
                        {getStatusIcon(status)}
                        <span className="ml-2">{status}</span>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Payment Info */}
              {selectedOrder?.paymentInfo && (
                <div className="border rounded-lg p-4">
                  <h3 className="font-medium mb-4">결제 정보</h3>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="flex items-center">
                      {getPaymentMethodInfo(selectedOrder.paymentInfo).icon}
                      <span className="ml-2 font-medium text-gray-800">{getPaymentMethodInfo(selectedOrder.paymentInfo).text}</span>
                      {selectedOrder.paymentInfo.receipt_url && (
                        <a href={selectedOrder.paymentInfo.receipt_url} target="_blank" rel="noopener noreferrer" className="ml-2 text-blue-600 hover:text-blue-800 text-sm inline-flex items-center">
                          <Receipt className="w-4 h-4 mr-1" />
                          매출전표
                        </a>
                      )}
                    </div>
                    <div className="ml-7 mb-2 text-xs text-gray-500">{getPaymentMethodInfo(selectedOrder.paymentInfo).details}</div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm mt-4">
                      <div className="md:border-r md:pr-3 md:text-center">
                        <p className="mb-1">
                          <span className="text-gray-500">결제일시:</span> {new Date(selectedOrder.paymentInfo.paid_at).toLocaleString()}
                        </p>
                      </div>
                      <div className="md:border-r md:pr-3 md:text-center">
                        <p className="mb-1">
                          <span className="text-gray-500">승인번호:</span> {selectedOrder.paymentInfo.approval_no}
                        </p>
                      </div>
                      <div className="md:pr-3 md:text-center">
                        <p className="mb-1">
                          <span className="text-gray-500">결제금액:</span> <span className="font-medium">{selectedOrder.paymentInfo.amount.toLocaleString()}원</span>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Customer Info */}
              <div className="border rounded-lg p-4">
                <h3 className="font-medium mb-4">고객 정보</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-500 mb-1">이름</label>
                    <input type="text" value={editingOrder?.user?.name || ""} onChange={(e) => handleOrderChange("user.name", e.target.value)} className="w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500" />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-500 mb-1">전화번호</label>
                    <input type="text" value={editingOrder?.user?.phone || ""} onChange={(e) => handleOrderChange("user.phone", e.target.value)} className="w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm text-gray-500 mb-1">이메일</label>
                    <input type="email" value={editingOrder?.user?.email || ""} onChange={(e) => handleOrderChange("user.email", e.target.value)} className="w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm text-gray-500 mb-1">우편번호</label>
                    <input type="text" value={editingOrder?.address?.zonecode || ""} onChange={(e) => handleOrderChange("address.zonecode", e.target.value)} className="w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm text-gray-500 mb-1">주소</label>
                    <input type="text" value={editingOrder?.address?.address || ""} onChange={(e) => handleOrderChange("address.address", e.target.value)} className="w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm text-gray-500 mb-1">상세주소</label>
                    <input type="text" value={editingOrder?.address?.address_detail || ""} onChange={(e) => handleOrderChange("address.address_detail", e.target.value)} className="w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500" />
                  </div>
                </div>
              </div>

              {/* Tracking Info */}
              <div className="border rounded-lg p-4">
                <h3 className="font-medium mb-4">배송 정보</h3>
                <div className="grid grid-col-1 gap-4">
                  <div className="flex-1">
                    <label className="block text-sm text-gray-500 mb-1">운송장 번호</label>
                    <input type="text" placeholder="운송장 정보 입력시 고객에게 배송 시작 알림이 발송됩니다" value={editingOrder?.tracking_no || ""} onChange={(e) => handleOrderChange("tracking_no", e.target.value)} className="w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500" />
                  </div>
                  <div className="flex-1">
                    <label className="block text-sm text-gray-500 mb-1">배송 메모</label>
                    <textarea type="text" value={editingOrder?.memo || ""} onChange={(e) => handleOrderChange("memo", e.target.value)} className="w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500" />
                  </div>
                </div>
              </div>

              {/* Order Items */}
              <div className="border rounded-lg p-4">
                <div className="flex items-center gap-2 mb-4">
                  <h3 className="font-medium">주문 상품</h3>
                  <button onClick={() => window.open(`${process.env.REACT_APP_API_BASE}/api/admin/order/${selectedOrder.uuid}/download_files`, "_blank")} className="text-gray-400 hover:text-teal-500 transition-colors p-1" title="모든 파일 다운로드">
                    <Download className="w-4 h-4" />
                  </button>
                </div>
                <div className="space-y-4">
                  {selectedOrder?.cart.items.map((item) => (
                    <CartItem key={item.item.uuid} item={item.item} editable={false} onRemove={() => {}} onUpdateQuantity={() => {}} initialQuantity={item.quantity} />
                  ))}
                </div>
              </div>

              {/* Order Summary */}
              <div className="border rounded-lg p-4">
                <h3 className="font-medium mb-4">주문 요약</h3>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>상품 금액</span>
                    <span>{selectedOrder?.cart.subtotal.toLocaleString()}원</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>배송비</span>
                    <span>{selectedOrder?.cart.shipping_fee === 0 ? "무료" : `${selectedOrder?.cart.shipping_fee.toLocaleString()}원`}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>추가 서비스 비용</span>
                    <span>{selectedOrder?.cart.additional_fee.toLocaleString()}원</span>
                  </div>

                  {/* Applied Coupon Info */}
                  {selectedOrder?.cart.applied_coupon && (
                    <div className="mt-2">
                      <div className="flex justify-between items-center text-sm">
                        <div className="flex items-center">
                          <span className="text-teal-500 font-semibold">{selectedOrder.cart.applied_coupon.couponName}</span>
                        </div>
                        <span className="text-teal-500 font-semibold">{selectedOrder.cart.applied_coupon.type === "PERCENTAGE" ? `-${selectedOrder.cart.applied_coupon.discountAmount}%` : `-${selectedOrder.cart.applied_coupon.discountAmount.toLocaleString()}원`}</span>
                      </div>
                      <div className="text-xs text-gray-400 mt-1 flex justify-between">
                        <span>쿠폰ID: {selectedOrder.cart.applied_coupon.couponId}</span>
                      </div>
                    </div>
                  )}

                  <div className="flex justify-between font-medium mt-2 pt-2 border-t">
                    <span>총 결제 금액</span>
                    <span className="text-teal-500">{selectedOrder?.cart.total_cost.toLocaleString()}원</span>
                  </div>
                </div>
              </div>

              {/* Update Button */}
              <div className="flex justify-end">
                <button onClick={updateOrder} disabled={isUpdatingOrder} className={`px-6 py-3 bg-teal-500 text-white rounded-lg hover:bg-teal-600 text-lg font-medium ${isUpdatingOrder ? "opacity-50 cursor-not-allowed" : ""}`}>
                  {isUpdatingOrder ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white mr-2"></div>
                      처리 중...
                    </div>
                  ) : (
                    "주문 정보 업데이트"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Coupon Modal */}
      {showCouponModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">{editingCoupon ? "쿠폰 수정" : "쿠폰 생성"}</h2>
              <button onClick={closeCouponModal} className="text-gray-500 hover:text-gray-700">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">쿠폰 ID</label>
                <input type="text" value={editingCoupon ? editingCoupon.couponId : newCoupon.couponId || ""} onChange={(e) => setNewCoupon({ ...newCoupon, couponId: e.target.value })} className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 ${editingCoupon ? "bg-gray-100 cursor-not-allowed" : ""}`} placeholder="쿠폰 ID (미입력시 자동생성)" disabled={!!editingCoupon} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">쿠폰 이름</label>
                <input type="text" value={newCoupon.couponName} onChange={(e) => setNewCoupon({ ...newCoupon, couponName: e.target.value })} className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">쿠폰 타입</label>
                <div className="grid grid-cols-2 gap-4">
                  <label className={`flex items-center space-x-2 p-3 rounded-lg cursor-pointer ${newCoupon.type === "PERCENTAGE" ? "bg-teal-50 border-teal-200" : "border border-gray-200 hover:border-teal-200"}`}>
                    <input type="radio" name="couponType" value="PERCENTAGE" checked={newCoupon.type === "PERCENTAGE"} onChange={(e) => setNewCoupon({ ...newCoupon, type: e.target.value })} className="text-teal-500 focus:ring-teal-500" />
                    <span>퍼센트 할인</span>
                  </label>
                  <label className={`flex items-center space-x-2 p-3 rounded-lg cursor-pointer ${newCoupon.type === "FIXED_AMOUNT" ? "bg-teal-50 border-teal-200" : "border border-gray-200 hover:border-teal-200"}`}>
                    <input type="radio" name="couponType" value="FIXED_AMOUNT" checked={newCoupon.type === "FIXED_AMOUNT"} onChange={(e) => setNewCoupon({ ...newCoupon, type: e.target.value })} className="text-teal-500 focus:ring-teal-500" />
                    <span>정액 할인</span>
                  </label>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">할인 금액</label>
                <input type="number" value={newCoupon.discountAmount} onChange={(e) => setNewCoupon({ ...newCoupon, discountAmount: Number(e.target.value) })} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">사용 가능 횟수</label>
                <input type="number" value={newCoupon.availableCount} onChange={(e) => setNewCoupon({ ...newCoupon, availableCount: Number(e.target.value) })} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500" />
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button onClick={closeCouponModal} disabled={isCreatingCoupon || isUpdatingCoupon} className="px-4 py-2 text-gray-600 hover:text-gray-800">
                  취소
                </button>
                <button onClick={editingCoupon ? updateCoupon : createCoupon} disabled={isCreatingCoupon || isUpdatingCoupon} className={`px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 ${isCreatingCoupon || isUpdatingCoupon ? "opacity-50 cursor-not-allowed" : ""}`}>
                  {isCreatingCoupon || isUpdatingCoupon ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                      처리 중...
                    </div>
                  ) : editingCoupon ? (
                    "수정"
                  ) : (
                    "생성"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-teal-600">성공</h2>
              <button onClick={() => setShowSuccessModal(false)} className="text-gray-500 hover:text-gray-700">
                <X className="w-6 h-6" />
              </button>
            </div>
            <p className="text-gray-700">{modalMessage}</p>
          </div>
        </div>
      )}

      {/* Error Modal */}
      {showErrorModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-red-600">오류</h2>
              <button onClick={() => setShowErrorModal(false)} className="text-gray-500 hover:text-gray-700">
                <X className="w-6 h-6" />
              </button>
            </div>
            <p className="text-gray-700">{modalMessage}</p>
          </div>
        </div>
      )}

      {/* Price Settings Modal */}
      {showPriceModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2">
          <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">단가 설정</h2>
              <button onClick={() => setShowPriceModal(false)} className="text-gray-500 hover:text-gray-700">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">시간당 단가 (원)</label>
                <input type="number" value={prices.time} onChange={(e) => handlePriceChange("time", e.target.value)} className="w-full px-3 py-2 border rounded-md" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">PLA 단가 (원/g)</label>
                <input type="number" value={prices.pla} onChange={(e) => handlePriceChange("pla", e.target.value)} className="w-full px-3 py-2 border rounded-md" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">ABS 단가 (원/g)</label>
                <input type="number" value={prices.abs} onChange={(e) => handlePriceChange("abs", e.target.value)} className="w-full px-3 py-2 border rounded-md" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">PC 단가 (원/g)</label>
                <input type="number" value={prices.pc} onChange={(e) => handlePriceChange("pc", e.target.value)} className="w-full px-3 py-2 border rounded-md" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">PA-CF 단가 (원/g)</label>
                <input type="number" value={prices.pacf} onChange={(e) => handlePriceChange("pacf", e.target.value)} className="w-full px-3 py-2 border rounded-md" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">TPU 단가 (원/g)</label>
                <input type="number" value={prices.tpu} onChange={(e) => handlePriceChange("tpu", e.target.value)} className="w-full px-3 py-2 border rounded-md" />
              </div>
            </div>
            <div className="mt-6 flex justify-end space-x-3">
              <button onClick={() => setShowPriceModal(false)} className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200">
                취소
              </button>
              <button onClick={updatePrices} disabled={isUpdatingPrices} className="px-4 py-2 text-white bg-teal-500 rounded-md hover:bg-teal-600 disabled:opacity-50">
                {isUpdatingPrices ? "저장 중..." : "저장"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Color Settings Modal */}
      {showColorModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2">
          <div className="bg-white rounded-lg p-6 w-full max-w-5xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">색상 설정</h2>
              <button onClick={() => setShowColorModal(false)} className="text-gray-500 hover:text-gray-700">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="mb-4">
              <div className="flex space-x-2 border-b">
                {["pla", "abs", "pc", "pacf", "tpu"].map((material) => (
                  <button key={material} onClick={() => setSelectedMaterial(material)} className={`px-4 py-2 font-medium ${selectedMaterial === material ? "border-b-2 border-teal-500 text-teal-600" : "text-gray-500 hover:text-gray-700"}`}>
                    {material === "pacf" ? "PA-CF" : material.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="md:col-span-1">
                <h3 className="text-lg font-medium mb-2">새 색상 추가</h3>
                <div className="mb-4">
                  <ChromePicker color={newColor} onChange={(color) => setNewColor(color.hex)} />
                </div>
                <button onClick={addColor} className="w-full px-4 py-2 text-white bg-teal-500 rounded-md hover:bg-teal-600">
                  색상 추가
                </button>
                <button
                  type="button"
                  onClick={() => {
                    addClearColor();
                  }}
                  className="mt-2 px-2 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300 w-full"
                >
                  투명 색상 추가
                </button>
              </div>
              <div className="md:col-span-3">
                <h3 className="text-lg font-medium mb-2">현재 색상 목록</h3>
                <div className="grid grid-cols-6 md:grid-cols-10 gap-2">
                  {colors[selectedMaterial]?.map((color, index) => (
                    <div key={index} className="relative group">
                      {color === "clear" ? (
                        <div
                          className="w-full h-16 rounded-md"
                          style={{
                            backgroundImage: 'url("/clear-pattern.svg")',
                            backgroundSize: "100%",
                            backgroundColor: "#fff",
                          }}
                        />
                      ) : (
                        <div className="w-full h-16 rounded-md" style={{ backgroundColor: color }} />
                      )}
                      <button onClick={() => removeColor(selectedMaterial, index)} className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="mt-6 flex justify-end space-x-3">
              <button onClick={() => setShowColorModal(false)} className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200">
                취소
              </button>
              <button onClick={updateColors} disabled={isUpdatingColors} className="px-4 py-2 text-white bg-teal-500 rounded-md hover:bg-teal-600 disabled:opacity-50">
                {isUpdatingColors ? "저장 중..." : "저장"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Custom Estimate Modal */}
      {showCustomEstimateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2">
          <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">커스텀 견적 생성</h2>
              <button onClick={() => setShowCustomEstimateModal(false)} className="text-gray-500 hover:text-gray-700">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">견적명</label>
                <input type="text" value={customEstimate.file_name} onChange={(e) => handleCustomEstimateChange("file_name", e.target.value)} className="w-full px-3 py-2 border rounded-md" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">소재</label>
                <select value={customEstimate.material} onChange={(e) => handleCustomEstimateChange("material", e.target.value)} className="w-full px-3 py-2 border rounded-md">
                  <option value="pla">PLA</option>
                  <option value="abs">ABS</option>
                  <option value="pacf">PA-CF</option>
                  <option value="pc">PC</option>
                  <option value="tpu">TPU</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">예상 비용 (원)</label>
                <input type="number" value={customEstimate.estimated_cost} onChange={(e) => handleCustomEstimateChange("estimated_cost", e.target.value)} className="w-full px-3 py-2 border rounded-md" />
              </div>
            </div>
            <div className="mt-6 flex justify-end space-x-3">
              <button onClick={() => setShowCustomEstimateModal(false)} className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200">
                취소
              </button>
              <button onClick={createCustomEstimate} disabled={isCreatingEstimate} className="px-4 py-2 text-white bg-teal-500 rounded-md hover:bg-teal-600 disabled:opacity-50">
                {isCreatingEstimate ? "생성 중..." : "생성"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPage;
